// All UI strings (Slovenian). Add an English file with the same shape later (type Strings).
// Rules: plain words, short sentences, no em dashes (use "-"), dates as "2. 12. 2027".

import type {
  AccountType,
  SystemAccount,
  AffectedGroup,
  ActionPriority,
  Audience,
  Autonomy,
  Category,
  DataCategory,
  DecisionRole,
  EmotionContext,
  EmployeesBand,
  Frequency,
  Light,
  Oversight,
  Role,
  Screening,
  Suite,
  SystemKind,
  SystemSource,
  SystemStatus,
  Tri,
  UseArea,
} from "../types";
import { withTerm } from "./termLayer";

/** Slovenian plural: forms for 1, 2, 3-4, 5+ (by n mod 100). */
export function plural(n: number, one: string, two: string, few: string, many: string): string {
  const m = Math.abs(n) % 100;
  if (m === 1) return one;
  if (m === 2) return two;
  if (m === 3 || m === 4) return few;
  return many;
}

export const sl = {
  brand: {
    product: "AI semafor",
    by: "Agentcija",
    agentcijaUrl: "https://agentcija.si",
    bookCallUrl: "https://tidycal.com/klemen-agentcija",
  },

  common: {
    loading: "Nalagam ...",
    save: "Shrani",
    saving: "Shranjujem ...",
    saved: "Shranjeno",
    cancel: "Prekliči",
    close: "Zapri",
    back: "Nazaj",
    next: "Naprej",
    skip: "Preskoči",
    done: "Končano",
    remove: "Odstrani",
    copy: "Kopiraj",
    copied: "Kopirano",
    retry: "Poskusi znova",
    yes: "Da",
    no: "Ne",
    optional: "neobvezno",
    newTab: "(odpre nov zavihek)",
    errorTitle: "Tega nismo mogli naložiti",
    errorGeneric: "Nekaj je šlo narobe. Poskusite znova čez trenutek.",
    networkError: "Strežnik se ne odziva. Preverite povezavo in poskusite znova.",
    notFound: "Te strani ni.",
    notFoundHint: "Povezava je morda stara ali napačno kopirana.",
    toHome: "Na začetno stran",
    demoBadge: "Demo podatki",
    demoBadgeHint: "Demo način: podatki so izmišljeni in se ne shranijo na strežnik.",
    aiSuggestion: "Predlog AI",
    aiSuggestionHint: "To je pripravil AI. Preverite in potrdite.",
    aiDraft: "Osnutek AI",
    all: "Vse",
    email: "E-pošta",
    yourEmail: "ime@podjetje.si",
    undo: "Razveljavi",
    open: "Odpri",
    /** keyboard shortcut that sends a form from a text area */
    sendShortcut: (mac: boolean) => (mac ? "⌘ + Enter" : "Ctrl + Enter"),
  },

  /** Long AI jobs that run on the server (plan, analysis, website profile). */
  jobs: {
    elapsed: (s: number) => `Traja že ${s} s`,
    usual: (s: number) => (s >= 55 ? `običajno približno ${Math.round(s / 60)} min` : `običajno približno ${s} s`),
    overtime: "Traja dlje kot običajno.",
    leaveOk: "Medtem lahko delate drugje v aplikaciji. Ko bo pripravljeno, vas obvestimo.",
    failed: "Ni uspelo. Poskusite znova.",
  },

  /** Calm messages for server limits and outages (shown wherever AI or the API is called). */
  errors: {
    rateLimited: "AI ima ta hip preveč dela ali pa je dosežena dnevna omejitev. Poskusite znova čez nekaj minut ali jutri. Kar ste vnesli, ostane shranjeno.",
    rateLimitedIn: (min: number) =>
      `AI ima ta hip preveč dela. Poskusite znova čez ${min} ${plural(min, "minuto", "minuti", "minute", "minut")}. Kar ste vnesli, ostane shranjeno.`,
    serverBusy: "Strežnik se tokrat ni odzval pravočasno. Poskusite znova čez trenutek.",
    /** 409: someone else saved the same entry first; the newest version is loaded */
    conflict: "Vnos je medtem spremenil nekdo drug. Naložili smo zadnjo različico, preverite jo.",
    conflictList: "Register je medtem spremenil nekdo drug. Naložili smo zadnje stanje.",
  },

  footer: {
    disclaimer: "Usmeritev, ne pravni nasvet.",
    legalStatus: (date: string) => `Aplikacija upošteva predpise, veljavne na dan ${date}.`,
    /** shown on hover: which rules the date covers */
    legalStatusHint:
      "Akt o umetni inteligenci (Uredba (EU) 2024/1689), kot ga je spremenila Uredba (EU) 2026/1744, in slovenski zakon o izvajanju akta (ZIUDHPUI).",
    madeBy: "AI semafor je brezplačno orodje podjetja Agentcija.",
    privacy: "Vaših podatkov ne prodajamo in jih ne uporabljamo za učenje modelov.",
    privacyLink: "Zasebnost in podatki",
  },

  /** /zasebnost: the privacy notice of the closed pilot. Plain words, no legalese. */
  privacy: {
    linkLabel: "Kako ravnamo s podatki",
    title: "Zasebnost in vaši podatki",
    lead:
      "AI semafor je v zaprtem pilotu.",
    updated: "Zadnja sprememba: 14. 10. 2026.",
    contactEmail: "nejc@agentcija.si",
    ipUrl: "https://www.ip-rs.si",
    ipLabel: "www.ip-rs.si",
    sections: [
      {
        id: "upravljavec",
        title: "Kdo skrbi za vaše podatke",
        paras: [
          "Upravljavec podatkov je Sellestial d.o.o., ki AI semafor ponuja pod znamko Agentcija.",
          "Za vsa vprašanja o podatkih pišite na nejc@agentcija.si.",
        ],
      },
      {
        id: "kaj",
        title: "Katere podatke hranimo",
        items: [
          "Prijava: vaš e-poštni naslov in ime, če ga vpišete.",
          "Profil podjetja: ime, domena, dejavnost, velikost in vaši odgovori o podjetju, na primer o svetu delavcev ali reguliranih področjih.",
          "Register AI: orodja in agenti, ki jih vpišete vi ali jih predlaga AI, z oceno, koraki in zgodovino sprememb.",
          "Pogovor z AI vodičem: vaša sporočila in odgovori vodiča.",
          "Anketa za zaposlene: odgovori so anonimni, razen če kdo vpiše svoje ime.",
          "Zapisi o usposabljanju: ime, oddelek in e-pošta, če jo kdo vpiše, rezultat kviza in potrditev pravilnika.",
          "Prijave nove rabe AI: opis ter ime in e-pošta, če ju kdo vpiše.",
          "Naložene datoteke: vsebino (na primer izvoz seznama orodij) hranimo samo, dokler jo obdelujemo. V registru ostanejo le vnosi, ki jih iz nje pripravi AI.",
        ],
        paras: ["Uporabljamo le piškotke, ki so nujni za prijavo in delovanje orodja. Piškotkov za oglaševanje ali sledenje ni."],
      },
      {
        id: "zakaj",
        title: "Zakaj jih uporabljamo",
        items: [
          "Da vam v pilotu zagotovimo orodje: popis AI, semafor, dokumente, anketo in usposabljanje.",
          "Da vas kontaktiramo glede rezultatov, a samo, če nas za to prosite.",
        ],
        paras: ["Podatkov ne prodajamo in jih ne uporabljamo za oglaševanje."],
      },
      {
        id: "kdo-vidi",
        title: "Kdo vidi podatke",
        items: [
          "Vaše podjetje: prijavljeni uporabniki podjetja vidijo vse njegove podatke, tudi odgovore ankete in zapise usposabljanj.",
          "Ekipa Agentcije: seznam podjetij v pilotu z imenom, velikostjo, dejavnostjo, e-pošto uporabnikov, številom vnosov po barvah, številom odgovorov ankete in idejami zaposlenih iz analize ankete.",
        ],
      },
      {
        id: "obdelovalci",
        title: "Kdo jih obdeluje za nas",
        items: [
          "Netlify: gostovanje aplikacije in baza podatkov. Funkcije in baza tečejo v Frankfurtu (EU).",
          "Anthropic: obdelava z AI prek API (modeli Claude). Podatki se lahko obdelujejo v ZDA. Anthropic podatkov iz API privzeto ne uporablja za učenje modelov.",
          "Resend: pošiljanje e-pošte s povezavo za prijavo (regija EU).",
        ],
        paras: [
          "AI dobi le besedilo, ki ga potrebuje za nalogo, na primer opis agenta, sporočila v pogovoru, odgovore ankete ali vsebino naložene datoteke.",
        ],
      },
      {
        id: "hramba",
        title: "Kako dolgo jih hranimo",
        items: [
          "Podatke podjetja hranimo, dokler jih podjetje ne izbriše ali dokler se pilot ne konča.",
          "Vsebino naloženih datotek in drugih AI opravil izbrišemo, ko je obdelava končana.",
          "Povezava za prijavo velja 30 minut in deluje samo enkrat.",
        ],
      },
      {
        id: "pravice",
        title: "Vaše pravice",
        items: [
          "Dostop: vprašate lahko, katere podatke hranimo o vas.",
          "Popravek: večino podatkov popravite kar v aplikaciji. Za ostalo nam pišite.",
          "Izbris: podatke podjetja izbrišete z gumbom Izbriši podatke podjetja v profilu podjetja ali nam pišete na nejc@agentcija.si.",
          "Pritožba: če menite, da podatke obdelujemo v nasprotju s predpisi, se lahko pritožite pri Informacijskem pooblaščencu (www.ip-rs.si).",
        ],
        paras: [
          "Zaposleni, ki so izpolnili anketo, usposabljanje ali prijavo nove rabe, se lahko obrnejo na skrbnika AI v svojem podjetju ali pišejo nam.",
        ],
      },
      {
        id: "nasvet",
        title: "Usmeritev, ne pravni nasvet",
        paras: [
          "AI semafor pomaga pri popisu AI in daje usmeritve po AI Actu. To ni pravni nasvet. Pri pomembnih odločitvah se posvetujte s pravnikom.",
        ],
      },
    ] as { id: string; title: string; items?: string[]; paras?: string[] }[],
  },

  lights: {
    green: "Zelena",
    yellow: "Rumena",
    red: "Rdeča",
    none: "Neocenjeno",
  } satisfies Record<Light | "none", string>,

  lightMeaning: {
    green: "Lahko uporabljate",
    yellow: "Uporabljajte, ko uredite korake",
    red: "Potreben je posvet",
    none: "Še ni ocene",
  } satisfies Record<Light | "none", string>,

  labels: {
    tri: { yes: "Da", no: "Ne", unknown: "Ne vem" } satisfies Record<Tri, string>,
    kind: {
      saas_tool: "AI orodje",
      saas_ai_feature: "AI v obstoječem orodju",
      internal_agent: "Lasten agent",
      custom_system: "Lasten AI sistem",
    } satisfies Record<SystemKind, string>,
    /** one sentence under the kind chips of the card */
    kindHint: {
      saas_tool: "Samostojna aplikacija z AI, na primer ChatGPT, DeepL ali Canva.",
      saas_ai_feature: "Funkcija AI v orodju, ki ga podjetje že uporablja za drugo delo, na primer Microsoft 365 Copilot ali Gemini v Google Workspace.",
      internal_agent: "Agent ali avtomatizacija za ponavljajočo se nalogo, ki jo je nastavil nekdo v podjetju, na primer v Copilot Studiu, n8n ali Make ali kot skill v Claude.",
      custom_system: "Lastna programska koda ali lasten model, na primer aplikacija, ki kliče API jezikovnega modela.",
    } satisfies Record<SystemKind, string>,
    audience: {
      internal_staff: "Samo zaposleni",
      customers: "Stranke",
      public: "Javnost",
      mixed: "Zaposleni in stranke",
      none: "Nihče neposredno",
    } satisfies Record<Audience, string>,
    affected: {
      employees: "Zaposleni",
      job_candidates: "Kandidati za zaposlitev",
      customers: "Stranke",
      public: "Javnost",
      children: "Otroci",
      patients: "Pacienti",
      students: "Učenci in študenti",
      suppliers: "Dobavitelji",
    } satisfies Record<AffectedGroup, string>,
    data: {
      public: "Javni podatki",
      internal: "Interni podatki",
      confidential: "Zaupni poslovni podatki",
      trade_secrets: "Poslovne skrivnosti",
      personal: "Osebni podatki",
      special_personal: "Občutljivi osebni podatki (zdravje ...)",
      customer_data: "Podatki strank",
    } satisfies Record<DataCategory, string>,
    decisionRole: {
      none: "Ne odloča o ljudeh",
      prepares: "Pripravi podlago, odloči človek",
      influences: "Bistveno vpliva na odločitev o osebi",
      decides: "Odloči sam ali brez pravega pregleda",
    } satisfies Record<DecisionRole, string>,
    oversight: {
      always_review: "Človek vedno pregleda",
      spot_check: "Občasni pregled",
      none: "Brez pregleda",
      unknown: "Ne vem",
    } satisfies Record<Oversight, string>,
    autonomy: {
      suggests: "Samo predlaga",
      acts_with_approval: "Ukrepa po potrditvi",
      acts_alone: "Ukrepa sam",
    } satisfies Record<Autonomy, string>,
    account: {
      company: "Poslovni paket",
      personal: "Paket za posameznike",
      mixed: "Oboje",
      unknown: "Ne vem",
    } satisfies Record<AccountType, string>,
    role: {
      deployer: "Uvajalec",
      provider: "Ponudnik",
      both: "Ponudnik in uvajalec",
    } satisfies Record<Role, string>,
    roleHint: {
      deployer: "Uporabljate sistem, ki ga je naredil nekdo drug.",
      provider: "Sistem ste zgradili sami ali ga uporabljate pod svojim imenom. Dodatne dolžnosti ponudnika veljajo pri visokem tveganju in pri preglednosti (obvestilo, da gre za AI, in označevanje ustvarjenih vsebin).",
      both: "Sistem ste zgradili in ga tudi uporabljate.",
    } satisfies Record<Role, string>,
    status: {
      proposed: "Predlog",
      confirmed: "Potrjeno",
      in_review: "V pregledu",
      retired: "Umaknjeno",
    } satisfies Record<SystemStatus, string>,
    source: {
      conversation: "Pogovor",
      survey: "Anketa",
      import: "Uvoz",
      catalog: "Katalog",
      agent_check: "Preverjanje agenta",
      intake: "Prijava zaposlenega",
      manual: "Ročni vnos",
    } satisfies Record<SystemSource, string>,
    category: {
      prohibited: "Prepovedana praksa",
      high_risk: "Visoko tveganje",
      transparency: "Obveznost preglednosti",
      minimal: "Nizko tveganje po AI Actu",
      not_ai: "Ni AI sistem",
    } satisfies Record<Category, string>,
    priority: {
      now: "Zdaj",
      before_go_live: "Pred zagonom",
      by_deadline: "Do roka",
      good_practice: "Dobra praksa",
    } satisfies Record<ActionPriority, string>,
    useArea: {
      none: "Nič od tega",
      hr_recruitment: "Zaposlovanje (oglasi, pregled življenjepisov, ocena kandidatov)",
      hr_management: "Upravljanje zaposlenih (napredovanje, odpoved, razporejanje, nadzor)",
      credit_scoring_consumers: "Kreditna sposobnost potrošnikov",
      insurance_life_health: "Življenjsko in zdravstveno zavarovanje",
      essential_services_access: "Dostop do javnih storitev in pomoči",
      emergency_dispatch: "Razvrščanje nujnih klicev",
      education: "Izobraževanje (sprejem, ocenjevanje, nadzor izpitov)",
      critical_infrastructure_safety: "Varnost kritične infrastrukture",
      biometrics: "Biometrija (prepoznavanje oseb, čustev)",
      law_enforcement_migration_justice: "Policija, migracije, pravosodje",
    } satisfies Record<UseArea, string>,
    emotionContext: {
      workplace: "Na delovnem mestu",
      education: "V izobraževanju",
      other: "Drugje",
      none: "Ne prepoznava čustev",
    } satisfies Record<EmotionContext, string>,
    suite: {
      microsoft365: "Microsoft 365",
      google_workspace: "Google Workspace",
      mixed: "Oboje",
      other: "Drugo",
      unknown: "Ne vem",
    } satisfies Record<Suite, string>,
    employees: {
      "1-19": "do 19",
      "20-49": "20-49",
      "50-249": "50-249",
      "250-499": "250-499",
      "500+": "500 in več",
    } satisfies Record<EmployeesBand, string>,
    frequency: {
      daily: "Vsak dan",
      weekly: "Vsak teden",
      monthly: "Nekajkrat na mesec",
      rarely: "Redko",
      never: "Nikoli",
    } satisfies Record<Frequency, string>,
    severity: { info: "Za vedenje", warn: "Pozor", risk: "Tveganje" },
    potential: { low: "Manjši potencial", medium: "Srednji potencial", high: "Velik potencial" },
    supervisor: {
      akos: "AKOS",
      ip: "Informacijski pooblaščenec",
      irsid: "IRSID",
    } as Record<string, string>,
  },

  /** Plain questions for each screening field (shown in the register drawer). */
  screening: {
    groups: [
      { id: "basic", title: "Osnova", fields: ["is_ai_system", "personal_data"] },
      {
        id: "prohibited",
        title: "Prepovedane prakse",
        ref: "čl. 5",
        fields: [
          "emotion_recognition",
          "emotion_context",
          "biometric_categorisation",
          "remote_biometric_identification",
          "manipulation_or_exploitation",
          "social_scoring",
        ],
      },
      {
        id: "high",
        title: "Odločitve o ljudeh",
        ref: "Priloga III",
        fields: ["use_areas", "profiling_natural_persons", "decision_role"],
      },
      {
        id: "transparency",
        title: "Preglednost",
        ref: "čl. 50",
        fields: [
          "interacts_with",
          "generates_public_content",
          "human_review_before_publication",
          "deepfakes",
          "offers_generative_system_to_others",
        ],
      },
      {
        id: "role",
        title: "Vaša vloga",
        ref: "čl. 25",
        fields: ["built_in_house", "own_name_on_system", "changed_intended_purpose"],
      },
    ] as { id: string; title: string; ref?: string; fields: (keyof Screening)[] }[],
    q: {
      is_ai_system: "Ali je to res AI (sam sklepa ali ustvarja vsebino iz podatkov)?",
      personal_data: "Ali obdeluje osebne podatke?",
      emotion_recognition: "Ali iz obraza, glasu ali telesa prepoznava čustva, razpoloženje ali namene ljudi?",
      emotion_context: "Kje prepoznava čustva?",
      biometric_categorisation: "Ali ljudi razvršča po biometričnih podatkih v občutljive skupine?",
      remote_biometric_identification: "Ali prepoznava ljudi na daljavo, npr. obraze na kamerah?",
      manipulation_or_exploitation: "Ali ljudi prikrito usmerja ali izkorišča njihove ranljivosti?",
      social_scoring: "Ali ljudi točkuje po vedenju za druge namene?",
      use_areas: "Ali ga uporabljate za kaj od tega?",
      profiling_natural_persons: "Ali ocenjuje ljudi (lastnosti, vedenje, uspešnost)?",
      decision_role: "Kakšno vlogo ima pri odločitvah o ljudeh?",
      interacts_with: "Kdo neposredno govori z njim ali dobi njegove izhode?",
      generates_public_content: "Ali objavlja AI besedila, ki obveščajo javnost o zadevah javnega interesa?",
      human_review_before_publication: "Ali človek pred objavo pregleda besedilo in zanj odgovarja?",
      deepfakes: "Ali ustvarja slike, zvok ali video, ki spominjajo na resnične ljudi ali dogodke?",
      offers_generative_system_to_others: "Ali generativni AI ponujate drugim, npr. strankam?",
      built_in_house: "Ali ste ga zgradili ali dali v uporabo sami?",
      own_name_on_system: "Ali ga ponujate pod svojim imenom ali znamko?",
      changed_intended_purpose: "Ali splošno orodje uporabljate za občutljiv namen, npr. ChatGPT za ocenjevanje kandidatov?",
    } satisfies Record<keyof Screening, string>,
  },

  /** Fields of the agent card (drawer + check result). */
  card: {
    title: "Izkaznica",
    name: "Ime",
    kind: "Vrsta",
    vendor: "Ponudnik",
    platform: "Platforma",
    purpose: "Za kaj se uporablja",
    purposePlaceholder: "Napišite, za kaj ga uporabljate pri vas.",
    purposeUses: "Pogosta raba:",
    purposeUsesHint: "Kliknite, da vpišete v polje. Nato shranite.",
    departments: "Oddelki",
    owner: "Lastnik",
    ownerEmail: "E-pošta lastnika",
    userCount: "Število uporabnikov",
    affected: "Koga zadeva",
    data: "Podatki",
    account: "Paket",
    autonomy: "Samostojnost",
    canDo: "Kaj lahko naredi",
    inputs: "Vhodni podatki in sprožilci",
    oversight: "Človeški nadzor",
    oversightNote: "Opomba o nadzoru",
    logging: "Beleži korake (dnevnik)",
    escalation: "Ko ni prepričan ali odpove",
    goLive: "Datum zagona",
    notes: "Opombe",
    empty: "Ni vpisano",
    addItemPlaceholder: "Vpišite in pritisnite Enter",
    runsOn: "Teče na",
    runsOnHint: "Orodje in model, na katerih teče agent, na primer n8n in OpenAI API ali Claude. Agent prevzame paket teh vnosov.",
    runsOnAdd: "Dodaj platformo",
    runsOnPick: "Izberite platformo",
    runsOnRegister: "V registru",
    runsOnCatalog: "Iz kataloga",
    runsOnNew: "doda se v register",
    runsOnRemove: (name: string) => `Odstrani ${name}`,
    runsOnMax: "Izbrali ste največje število platform (3).",
    runsOnHost: (names: string) => `Na tem vnosu teče ${names}, zato sam ne more teči na drugem vnosu.`,
    runsOnOld: (text: string) => `Prej vpisano: ${text}`,
    accountFromPlatform: (plan: string) => `${plan}, prevzeto s platforme.`,
    accountOnSave: "Paket se prevzame s platforme, ko shranite.",
    agentsOn: "Agenti na tej platformi",
    ownEntry: "Zakaj ima ta agent svoj vnos",
    ownEntryReasons: {
      acts_alone: "Sam ukrepa, brez potrditve človeka.",
      outside: "Komunicira z ljudmi zunaj podjetja.",
      people: "Pripravlja ali sprejema odločitve o ljudeh.",
      personal_data: "Obdeluje osebne podatke.",
      own_name: "Deluje pod imenom podjetja.",
    },
    ownEntryWhy: "AI Act obveznosti veže na posamezen sistem in njegov namen, zato ga vodite posebej.",
    ownEntryNone:
      "Ta agent ne izpolnjuje nobenega pogoja za svoj vnos: ne ukrepa sam, ne komunicira z ljudmi zunaj podjetja, ne odloča o ljudeh, ne obdeluje osebnih podatkov in ne deluje pod imenom podjetja. Zadošča, da ga zapišete kot rabo pri platformi, na kateri teče (polje »Za kaj se uporablja«). Ta vnos lahko odstranite ali ga pustite, če ga želite voditi posebej.",
    ownEntryUnknown: "Ali ta agent potrebuje svoj vnos, bo jasno, ko vpišete samostojnost in podatke ter odgovorite na vprašanja v zavihku »Vprašanja«.",
  },

  nav: {
    landingHow: "Kako deluje",
    landingWhen: "Kaj velja kdaj",
    login: "Prijava",
    toApp: "Odpri aplikacijo",
    overview: "Pregled",
    chat: "Pogovor",
    register: "Register",
    survey: "Anketa",
    literacy: "Pismenost",
    documents: "Dokumenti",
    admin: "Vsa podjetja",
    logout: "Odjava",
    menu: "Meni",
    skip: "Preskoči na vsebino",
    mainNav: "Glavna navigacija",
    appNav: "Aplikacija",
  },

  landing: {
    heroTitle: "Ne ustavljajte AI. Uvedite semafor.",
    heroTitleLines: ["Ne ustavljajte AI.", "Uvedite semafor."],
    heroSub:
      "Popis AI v podjetju, semafor za vsakega agenta in vsi dokumenti po AI Actu. Brezplačno, v slovenščini.",
    heroWho:
      "Za podjetja, ki že uporabljajo AI (ChatGPT, Copilot, lastne agente), in za osebo, ki v podjetju skrbi zanj.",
    heroWhoLabel: "Komu je namenjeno",
    ctaCheck: "Preveri agenta v 3 minutah",
    ctaInventory: "Začni popis podjetja",
    ctaNote: "Brez prijave. E-pošto vprašamo šele, ko želite shraniti.",
    examplesLabel: "Primeri agentov",
    examples: [
      {
        id: "meetings",
        tab: "Povzetki sestankov",
        name: "Agent, ki povzame sestanke v Teams",
        light: "green" as Light,
        headline: "Lahko uporabljate. Velja osnovno: pismenost in vpis v register.",
        steps: ["Vpišite ga v register", "Zaposlene naučite, kako preveriti povzetek"],
      },
      {
        id: "support",
        tab: "Klepetalnik za stranke",
        name: "Agent, ki na spletni strani odgovarja strankam",
        light: "yellow" as Light,
        headline: "Uporabljate lahko, ko uredite 2 koraka.",
        steps: ["Na začetku pogovora povejte, da gre za AI", "Določite, kdo pregleda zahtevnejše odgovore"],
      },
      {
        id: "hiring",
        tab: "Ocena kandidatov",
        name: "Agent, ki razvrsti kandidate za službo",
        light: "red" as Light,
        headline: "Visoko tveganje: potrebna je priprava do 2. 12. 2027 in posvet.",
        steps: ["Odločitev naj vedno sprejme človek", "Obvestite kandidate in svet delavcev"],
      },
    ],
    todayTitle: "Kaj velja danes",
    todaySub: "Večina notranjih agentov ima danes le lahke dolžnosti. Zamrznitev nima podlage v AI Actu.",
    today: [
      {
        title: "AI pismenost",
        text: "Podjetje mora z ukrepi podpirati AI pismenost ljudi, ki delajo z AI. Dovolj so smiselni ukrepi in evidenca o njih, certifikat ni potreben.",
        ref: "čl. 4, od 2. 2. 2025",
      },
      {
        title: "Prepovedane prakse",
        text: "Nekatere rabe so prepovedane, npr. prepoznavanje čustev zaposlenih. Preverite, da jih nimate.",
        ref: "čl. 5, od 2. 2. 2025",
      },
      {
        title: "Preglednost",
        text: "Ljudje morajo vedeti, kdaj govorijo z AI. Ponarejeni posnetki morajo biti označeni.",
        ref: "čl. 50, od 2. 8. 2026",
      },
      {
        title: "Visoko tveganje prihaja",
        text: "AI pri zaposlovanju, kreditih ali zavarovanjih bo imel stroge zahteve. Popis vam da čas za pripravo.",
        ref: "Priloga III, od 2. 12. 2027",
      },
    ],
    getTitle: "Kaj dobite",
    getSub: "Dokumenti nastanejo iz vašega registra, zato so vedno usklajeni. Prenesete jih v Wordu in Excelu.",
    docs: [
      { title: "Register AI sistemov", format: "xlsx", text: "Vsa orodja in agenti z oceno, lastnikom in datumom pregleda." },
      { title: "Pravilnik o rabi AI", format: "docx", text: "Kaj je dovoljeno, s katerimi podatki in kdo odobri nove agente." },
      { title: "Načrt AI pismenosti", format: "docx", text: "Kdo se mora česa naučiti, po vlogah." },
      { title: "Evidenca usposabljanj", format: "xlsx", text: "Kdo je opravil usposabljanje in potrdil pravilnik." },
      { title: "Besedila obvestil", format: "docx", text: "Obvestila za klepetalnike, označbe AI vsebin, izjava za splet." },
      { title: "Poročilo za upravo", format: "docx", text: "Stanje na eni strani, s semaforjem in roki." },
      { title: "Izkaznice agentov", format: "docx", text: "Za vsakega agenta: kaj dela, kdo ga nadzira, kaj sme." },
    ],
    howTitle: "Kako deluje",
    how: [
      {
        title: "Povejte, kaj uporabljate",
        text: "AI prebere vašo spletno stran, predlaga orodja iz kataloga in vpraša le, česar ne ve. Vi kliknete in popravite.",
      },
      {
        title: "Dobite semafor za vsako orodje",
        text: "Zelena, rumena ali rdeča, s konkretnimi koraki. Barvo določijo zapisana pravila, ne AI.",
      },
      {
        title: "Prenesite dokumente in jih sproti posodabljajte",
        text: "Anketa za zaposlene, usposabljanje z evidenco in semafor za vsakega novega agenta pred zagonom.",
      },
    ],
    whenTitle: "Kaj velja kdaj",
    whenSub: "Datumi po zadnji spremembi AI Acta (Digital Omnibus, Uredba 2026/1744).",
    today_: "Danes",
    skillTitle: "Semafor tudi v vašem AI asistentu",
    skillText:
      "Prenesite dodatek (skill) za Claude, Copilot ali ChatGPT. Kdor gradi agente, jih preveri kar med delom, z istimi pravili.",
    skillCta: "Prenesi dodatek",
    skillNote: "Pošljemo vam povezavo za prijavo. Po prijavi se prenos začne sam.",
    agentcijaTitle: "Za semaforjem stoji Agentcija",
    agentcijaText:
      "Gradimo AI agente za slovenska podjetja: z merjenim učinkom, znotraj EU in s človekom, ki potrdi. Če vam popis pokaže priložnosti, se pogovorimo.",
    agentcijaCta: "Rezerviraj 30-minutni klic",
    agentcijaSite: "agentcija.si",
  },

  check: {
    title: "Preveri agenta",
    builderTitle: (org: string) => `Preveri agenta za ${org}`,
    intro: "AI iz opisa pripravi izkaznico agenta in vas vpraša le, kar vpliva na oceno.",
    builderIntro: "Agent gre v register podjetja kot predlog. Skrbnik AI ga pregleda.",
    builderName: "Vaše ime (neobvezno)",
    builderEmail: "Vaša e-pošta (neobvezno)",
    builderWhoHint: "Skrbnik AI bo pri agentu videl, kdo ga je prijavil, in vas lahko kaj vpraša.",
    label: "Opišite agenta s svojimi besedami ali prilepite njegova navodila (npr. iz Copilot Studia)",
    placeholder:
      "Npr.: Agent v Copilot Studiu odgovarja strankam na spletni strani o stanju pošiljk. Bere podatke iz našega ERP in lahko odpre reklamacijo.",
    examplesTitle: "Ali začnite s primerom",
    examples: [
      "Agent v Copilot Studiu odgovarja strankam na spletni strani o stanju pošiljk in odpre reklamacijo.",
      "Avtomatizacija v n8n prebere prejete račune, izpiše podatke in jih vnese v ERP.",
      "GPT pregleda življenjepise kandidatov in jih razvrsti po ustreznosti za delovno mesto.",
    ],
    submit: "Pripravi izkaznico",
    minChars: "Opis je prekratek. Napišite vsaj en stavek.",
    drafting: [
      "Berem opis agenta",
      "Iščem, kaj agent dela in s katerimi podatki",
      "Preverjam pravila AI Acta",
      "Pripravljam vprašanja, ki vplivajo na oceno",
    ],
    draftTitle: "Osnutek izkaznice",
    questionOf: (i: number, n: number) => `Vprašanje ${i} od ${n}`,
    whyAsk: "Zakaj sprašujemo",
    finish: "Pokaži oceno",
    answering: ["Vpisujem odgovore", "Računam semafor po pravilih", "Pišem razlago"],
    resultTitle: "Ocena agenta",
    obligations: "Kaj velja zanj",
    actions: "Koraki",
    reasoning: "Zakaj ta barva",
    roleLabel: "Vaša vloga",
    rulesNote: (date: string) => `Barvo določijo pravila (stanje ${date}), AI napiše le razlago.`,
    saveTitle: "Shrani v register in prenesi izkaznico",
    saveText: "Na e-pošto vam pošljemo povezavo za prijavo. Po prijavi se agent shrani v register vašega podjetja.",
    saveCta: "Shrani in prenesi izkaznico",
    saveLoggedIn: "Shrani v register",
    saveBuilderTitle: (org: string) => `Register podjetja ${org}`,
    saveBuilderCta: "Pošlji v register",
    savedTitle: "Shranjeno v register",
    savedText: "Agent je v registru kot predlog. Skrbnik AI ga pregleda in potrdi.",
    downloadCard: "Prenesi izkaznico (.docx)",
    openRegister: "Odpri register",
    startOver: "Preveri drugega agenta",
    editAnswers: "Spremeni odgovore",
    guessNote: "AI je odgovor predlagal iz vašega opisa. Preverite ga in po potrebi spremenite.",
    appliesFrom: (d: string) => `Velja od ${d}`,
    supervisor: (s: string) => `Nadzor: ${s}`,
    how: "Kako",
    needOrgLead: "Vpišite še podatke o podjetju. Nato agenta shranimo v register vašega podjetja.",
  },

  login: {
    title: "Prijava",
    sub: "Gesla ne potrebujete. Na e-pošto vam pošljemo povezavo za prijavo.",
    emailLabel: "Službena e-pošta",
    submit: "Pošlji povezavo",
    sending: "Pošiljam ...",
    sentTitle: "Preverite e-pošto",
    sentTo: "Povezavo smo poslali na",
    sentHint: "Odprite e-pošto in kliknite povezavo. Velja 30 minut.",
    devLink: "Demo: prijava brez e-pošte",
    otherEmail: "Uporabi drug naslov",
    resend: "Pošlji znova",
    resendIn: (s: number) => `Pošlji znova čez ${s} s`,
    resending: "Pošiljam ...",
    resent: "Poslali smo novo povezavo.",
    helpTitle: "E-pošte ni?",
    helpItems: [
      "Počakajte minuto ali dve.",
      "Poglejte v mapo z vsiljeno pošto (spam) ali v zavihek Promocije.",
      "Preverite, ali je naslov pravilen.",
    ],
    helpContact: "Če povezave še vedno ni, pišite na",
    contactEmail: "nejc@agentcija.si",
    unavailableTitle: "Prijava po e-pošti še ne deluje",
    unavailableText: "Za ročno prijavo pišite na",
    rateTitle: "Preveč poskusov",
    invalidEmail: "Vpišite veljaven e-poštni naslov.",
    why: "E-pošto potrebujemo za prijavo in shranjevanje vašega registra.",
  },

  /** /prijava/potrdi: the login link from the e-mail is used only after a click. */
  confirmLogin: {
    title: "Prijava v AI semafor",
    cta: "Prijavi se",
    busy: "Prijavljam ...",
    why: "Klik je potreben, ker varnostni pregledi e-pošte povezave odpirajo sami. Tako vaše povezave ne porabijo namesto vas.",
    newLink: "Pošlji novo povezavo",
    problemTitle: {
      used: "Ta povezava je že porabljena",
      expired: "Povezava je potekla",
      invalid: "Povezava ne deluje",
    },
    problemText: {
      used: "Vsaka povezava za prijavo deluje samo enkrat. Morda ste se z njo že prijavili, na primer v drugem brskalniku.",
      expired: "Povezava za prijavo velja 30 minut. Ta je starejša.",
      invalid: "Povezava je nepopolna ali napačno kopirana.",
    },
  },

  /** Any API answer 403 {code: "revoked"}: pilot access was removed. */
  revoked: {
    title: "Dostop do pilota je bil umaknjen",
    text: "Odjavili smo vas in izbrisali podatke, ki jih je hranil ta brskalnik.",
    contact: "Če mislite, da gre za napako, ali želite dostop nazaj, pišite na",
  },

  survey: {
    titleFor: (org: string) => `Anketa o rabi AI v podjetju ${org}`,
    intro:
      "Pomagajte nam ugotoviti, katera AI orodja uporabljamo in kje bi nam AI prihranil čas. Traja 3 do 5 minut. Odgovori so anonimni, razen če sami vpišete svoje ime.",
    /** who sees the answers: second paragraph of the intro */
    introWho:
      "Odgovore vidijo sodelavci, ki v podjetju vodijo AI semafor. Agentcija, ki orodje upravlja, vidi le število odgovorov in ideje za rabo AI, brez imen.",
    start: "Začni anketo",
    stepOf: (i: number, n: number) => `${i} / ${n}`,
    progress: "Napredek ankete",
    qDepartment: "V katerem oddelku delate?",
    qDepartmentOther: "Drug oddelek",
    qTools: "Katera AI orodja uporabljate pri delu?",
    qToolsHint: "Označite vse, tudi tista, ki jih uporabljate le občasno.",
    qToolsNone: "Ne uporabljam AI",
    qToolsOther: "Druga orodja",
    qToolsOtherPh: "Npr. Gamma, Napkin",
    qToolsPopular: "Najpogostejša",
    qToolsShowAll: (n: number) => `Pokaži vsa orodja (${n})`,
    qToolsShowLess: "Pokaži manj",
    qToolsSearch: "Poiščite orodje",
    qToolsNoMatch: "Tega orodja ni na seznamu. Vpišite ga spodaj pod Druga orodja.",
    qToolsSelected: (n: number) => `Izbrano: ${n}`,
    qUses: "Za kaj jih največ uporabljate?",
    qUsesHint: "Kliknite primere ali napišite v enem ali dveh stavkih.",
    qUsesExamples: ["Pisanje e-pošte", "Prevajanje", "Povzetki dokumentov", "Iskanje informacij", "Analiza podatkov"],
    qUsesPh: "Npr. pišem odgovore strankam in prevajam ponudbe",
    qData: "Katere podatke vpisujete v AI orodja?",
    qDataHint: "Iskreno. Odgovor pomaga nastaviti pravila, ne iskati krivcev.",
    qAccount: "Kateri paket uporabljate?",
    qAccountHint:
      "Poslovni paket ima podjetje s ponudnikom, na primer ChatGPT Business ali Microsoft 365 Copilot. Paket za posameznike je na primer brezplačni ChatGPT, ChatGPT Plus ali Claude Pro, tudi če ga plača podjetje.",
    qFrequency: "Kako pogosto?",
    qIdeas: "Kje bi vam AI prihranil čas?",
    qIdeasHint: "Katero opravilo vam vzame največ časa in bi ga radi predali?",
    qIdeasPh: "Npr. ročno prepisovanje naročil iz e-pošte v ERP",
    qTraining: "Bi želeli kratko usposabljanje o varni rabi AI?",
    submit: "Pošlji odgovore",
    thanksTitle: "Hvala!",
    thanksText:
      "Odgovori so shranjeni. Pomagali bodo pri pravilih za rabo AI in pri iskanju opravil, ki jih lahko prevzame AI.",
    thanksClose: "Okno lahko zaprete.",
    invalid: "Ta povezava do ankete ne deluje več. Prosite skrbnika AI za novo.",
  },

  intake: {
    titleFor: (org: string) => `Prijavi novo rabo AI v podjetju ${org}`,
    intro: "Začeli ste uporabljati novo AI orodje ali zgradili agenta? Opišite ga in skrbnik AI bo poskrbel za ostalo.",
    label: "Kaj uporabljate in za kaj?",
    placeholder: "Npr.: V prodaji smo začeli uporabljati Fireflies za zapisnike sestankov s strankami.",
    nameLabel: "Vaše ime",
    emailLabel: "E-pošta, če želite odgovor",
    submit: "Pošlji prijavo",
    sending: "Pošiljam ...",
    thanksTitle: "Hvala, prijava je oddana",
    thanksText: "AI jo vpiše v register kot predlog. Skrbnik AI jo pregleda.",
    failed: "Prijava se ni shranila. Besedilo je ostalo, poskusite znova.",
    another: "Prijavi še eno",
  },

  training: {
    titleFor: (org: string) => `Usposabljanje AI za ${org}`,
    // the duration is shown next to it from the module itself (the plan may change it)
    intro: "Na koncu sta kratek kviz in potrditev pravilnika. Ime in rezultat se shranita v evidenco usposabljanj podjetja.",
    nameLabel: "Ime in priimek",
    nameRequired: "Vpišite ime in priimek.",
    deptLabel: "Oddelek",
    emailLabel: "E-pošta",
    start: "Začni usposabljanje",
    minutes: (n: number) => `${n} min`,
    objectives: "Po usposabljanju boste znali",
    // the module as slides: one section per slide
    slidesNav: "Strani gradiva",
    slideOf: (i: number, n: number) => `Stran ${i} od ${n}`,
    continued: "nadaljevanje",
    keysHint: "Uporabite lahko tudi tipki ← in →.",
    quizAhead: (n: number) => `Sledi kratek kviz z ${n} ${plural(n, "vprašanjem", "vprašanjema", "vprašanji", "vprašanji")}.`,
    toModule: "Gradivo",
    toQuiz: "Na kviz",
    questionOf: (i: number, n: number) => `Vprašanje ${i} od ${n}`,
    check: "Preveri",
    checking: "Preverjam ...",
    correct: "Pravilno.",
    wrong: "Ni pravilno.",
    nextQuestion: "Naslednje vprašanje",
    toPolicy: "Na pravilnik",
    policyTitle: "Pravilnik o rabi AI",
    policyIntro: "Na kratko, kar velja v vašem podjetju:",
    policyAck: "Prebral(a) sem pravilnik o rabi AI in ga bom upošteval(a).",
    finish: "Zaključi usposabljanje",
    finishing: "Shranjujem ...",
    doneTitle: "Usposabljanje je opravljeno",
    failTitle: "Usposabljanje še ni opravljeno",
    score: (s: number, t: number) => `${s} od ${t} pravilnih odgovorov`,
    doneText: "Vaše usposabljanje je zabeleženo v evidenci podjetja.",
    failText: "Za uspešno opravljeno potrebujete vsaj 70 %. Preberite gradivo še enkrat in poskusite znova.",
    retry: "Poskusi znova",
    refresh: "Osveži",
    refreshing: "Osvežujem ...",
    progress: "Napredek usposabljanja",
    steps: ["Podatki", "Gradivo", "Kviz", "Pravilnik"],
  },

  app: {
    hello: (name: string) => `Dober dan, ${name}`,
    helloPlain: "Dober dan",
  },

  onboarding: {
    title: "Najprej vaše podjetje",
    sub: "AI prebere vašo spletno stran in pripravi profil podjetja. Vi ga le preverite.",
    domainLabel: "Domena podjetja",
    domainPh: "podjetje.si",
    submit: "Preberi spletno stran",
    manual: "Izpolni ročno",
    reading: (d: string) => [`Berem spletno stran ${d}`, "Preverjam e-pošto in pisarniški paket", "Pripravljam profil"],
    readingDemo: (d: string) => [`Demo: spletne strani ${d} ne beremo`, "Pripravljam prazen profil"],
    readingEta: 35,
    readingNameLabel: "Medtem vpišite ime podjetja",
    readingNameHint: "Ni obvezno. Ime, ki ga vpišete, ima prednost pred predlogom AI.",
    cardTitle: "Je to vaše podjetje?",
    cardSub: "Kar je označeno s Predlog AI, smo našli na spletni strani. Preverite in popravite.",
    cardSubNone: "Na spletni strani nismo našli zanesljivih podatkov. Izpolnite, kar veste, ostalo lahko pustite prazno.",
    notFound: "Tega nismo našli. Vpišite ali pustite prazno.",
    notFoundPick: "Tega nismo našli. Izberite ali pustite prazno.",
    notFoundName: "Imena nismo našli. Vpišite ga.",
    sourceNote: "Vir",
    readFailed: "Spletne strani nismo mogli prebrati.",
    readSlow: "Branje spletne strani je trajalo predolgo.",
    name: "Ime podjetja",
    industry: "Dejavnost",
    description: "Opis",
    size: "Število zaposlenih",
    suite: "Pisarniški paket",
    publicBody: "Javni organ ali javna služba",
    critical: "Kritična infrastruktura",
    sectors: "Regulirana področja",
    sectorsPh: "Npr. finance, zdravstvo, energetika",
    workers: "Svet delavcev ali sindikat",
    owner: "Kdo skrbi za AI",
    ownerPh: "Ime in priimek",
    ownerEmail: "E-pošta skrbnika AI",
    ownerEmailPh: "Npr. ai@podjetje.si",
    ownerEmailHint: "Naslov za vprašanja o AI v pravilniku in obvestilih.",
    ownerEmailInvalid: "Vpišite veljaven e-poštni naslov skrbnika AI.",
    save: "Potrdi profil",
    saving: "Shranjujem ...",
    manualSub: "Izpolnite, kar veste. Polja, ki jih ne poznate, lahko pustite prazna.",
    domainInvalid: "Vpišite domeno, npr. podjetje.si.",
    nameRequired: "Vpišite ime podjetja.",
    profileTitle: "Profil podjetja",
    profileSaved: "Profil je shranjen.",
    ownAgents: "Lastni agenti in avtomatizacije",
    ownAgentsOptions: { yes: "Imamo", no: "Nimamo", unknown: "Ne vem" } satisfies Record<Tri, string>,
    /** the choices "AI" and "UI" are shown as they are (never through the term) */
    aiTerm: "Izraz za umetno inteligenco",
    aiTermHint: "Velja v aplikaciji, dokumentih in na straneh za zaposlene.",
  },

  /** Profile page, danger section: DELETE /org {confirm_name}. */
  deleteOrg: {
    title: "Izbriši podatke podjetja",
    text: "Izbrišemo profil podjetja, register AI, pogovor z AI vodičem, odgovore ankete, zapise usposabljanj in prijave nove rabe. Povezave za zaposlene prenehajo delovati. Tega ni mogoče razveljaviti.",
    hint: "Dokumente, ki jih želite obdržati, prej prenesite na strani Dokumenti.",
    cta: "Izbriši podatke podjetja",
    modalTitle: "Res izbrišem vse podatke podjetja?",
    confirmLabel: "Za potrditev vpišite ime podjetja",
    mismatch: "Ime se ne ujema. Vpišite ga tako, kot je zapisano zgoraj.",
    submit: "Izbriši za vedno",
    deleting: "Brišem ...",
    doneTitle: "Podatki podjetja so izbrisani",
    doneText: (name: string) => `Podatke podjetja ${name} smo izbrisali in vas odjavili.`,
    doneContact: "Če imate vprašanje, pišite na",
  },

  /** "Nova povezava" next to each share link: POST /org/tokens/rotate {kind}. */
  rotate: {
    action: "Nova povezava",
    actionLabel: (what: string) => `Nova povezava: ${what}`,
    confirm: "Stara povezava bo takoj prenehala delovati.",
    submit: "Ustvari novo povezavo",
    busy: "Ustvarjam ...",
    done: "Nova povezava je pripravljena. Stara ne deluje več.",
  },

  overview: {
    title: "Pregled",
    progressTitle: "Kje ste",
    progressSub: "Vsak korak lahko prekinete in nadaljujete pozneje.",
    nextStep: "Naslednji korak",
    allDone: "Vse osnovno je urejeno. Register preglejte vsaj enkrat na leto.",
    lightsTitle: "Semafor registra",
    lightsEmpty: "Register je še prazen. Najhitreje ga napolnite v pogovoru z AI vodičem.",
    systemsTotal: (n: number) => `${n} ${plural(n, "vnos", "vnosa", "vnosi", "vnosov")} v registru`,
    deadlinesTitle: "Roki",
    daysLeft: (n: number) =>
      n < 0 ? "že velja" : n === 0 ? "danes" : `čez ${n} ${plural(n, "dan", "dneva", "dni", "dni")}`,
    actionsTitle: "Odprti koraki",
    actionsCount: (n: number) => `${n} ${plural(n, "odprt korak", "odprta koraka", "odprti koraki", "odprtih korakov")}`,
    actionsEmpty: "Ni odprtih korakov.",
    actionsEmptyNoSystems: "Koraki se pojavijo, ko v register dodate prvo orodje ali agenta.",
    actionsAll: "Vsi koraki v registru",
    /** secondary button in the next step "Lastni agenti": saves has_own_agents "no" */
    noOwnAgents: "Nimamo lastnih agentov",
    proposedNote: (n: number) =>
      `${n} ${plural(n, "predlog čaka", "predloga čakata", "predlogi čakajo", "predlogov čaka")} na vašo potrditev`,
    review: "Preglej",
    confirmGreen: (n: number) => `Potrdi zelene (${n})`,
    moreSystems: (n: number) => `in še ${n}`,
  },

  /** The catalog picker (chat and "Dodaj iz kataloga" in the register): products, account types, own tools. */
  catalog: {
    accountRow: "Paket",
    /** the account question under a ticked product: "<b>Claude</b>: kateri paket uporabljate?" */
    accountQuestion: "kateri paket uporabljate?",
    account: {
      personal: "Paket za posameznike",
      business: "Poslovni paket",
      unknown: "Ne vem",
    } as Record<SystemAccount, string>,
    /** the tooltip of an account chip */
    accountHint: {
      personal: "Paket za posameznike, tudi če ga plača podjetje: brez pogodbe o obdelavi podatkov in brez skrbniške konzole.",
      business: "Paket pod poslovnimi pogoji ponudnika: s pogodbo o obdelavi podatkov in skrbniško konzolo.",
      unknown: "V register zapišemo korak: preverite, kateri paket uporabljajo zaposleni.",
    } as Record<SystemAccount, string>,
    /** the account in an entry name or a sentence: "ChatGPT (paket za posameznike)" */
    accountSuffix: {
      personal: "paket za posameznike",
      business: "poslovni paket",
      unknown: "paket ni znan",
    } as Record<SystemAccount, string>,
    plansHint: (personal: string, business: string) => `Za posameznike: ${personal}. Poslovni: ${business}.`,
    bothHint: "Šteje paket, ne kdo ga plača. Izberete lahko oboje.",
    /** the note next to the confirm button: ticked tools without an account choice */
    missingAccount: (names: string[]) =>
      names.length === 1
        ? `Pri orodju ${names[0]} paket ni izbran. Zapišemo »Ne vem«.`
        : names.length <= 3
          ? `Pri orodjih ${names.slice(0, -1).join(", ")} in ${names[names.length - 1]} paket ni izbran. Zapišemo »Ne vem«.`
          : `Pri ${names.length} orodjih paket ni izbran. Zapišemo »Ne vem«.`,
    showMissing: "Pokaži",
    addCustom: "Dodaj orodje, ki ga ni na seznamu",
    addQuery: (q: string) => `Dodaj »${q}«`,
    customLabel: "Ime orodja",
    customPh: "Npr. Gamma",
    customAdd: "Dodaj",
    customCancel: "Prekliči",
    customBadge: "ni na seznamu",
    remove: (name: string) => `Odstrani ${name}`,
    noMatch: "Takega orodja ni na seznamu.",
    inRegister: "v registru",
    inRegisterWith: (accounts: string[]) => `v registru: ${accounts.join(", ")}`,
    /** short account words for the "v registru" mark on a chip */
    accountShort: {
      personal: "za posameznike",
      business: "poslovni",
      unknown: "ne vem",
    } as Record<SystemAccount, string>,
    /** "paket za posameznike in poslovni paket" for a custom tool in a sentence */
    accountPhrase: (accounts: SystemAccount[]) => {
      const p = accounts.includes("personal");
      const b = accounts.includes("business");
      if (p && b) return "paket za posameznike in poslovni paket";
      if (p) return "paket za posameznike";
      if (b) return "poslovni paket";
      return "paket ni znan";
    },
    /** custom tools for the AI guide in the chat answer */
    customMessage: (items: string[]) =>
      items.length === 1 ? `Orodje, ki ga ni na seznamu: ${items[0]}.` : `Orodja, ki jih ni na seznamu: ${items.join(", ")}.`,
    /** custom tools for "Dodaj z opisom" (the register modal) */
    customText: (items: string[]) =>
      items.length === 1
        ? `Uporabljamo orodje, ki ga ni na seznamu: ${items[0]}.`
        : `Uporabljamo orodja, ki jih ni na seznamu: ${items.join(", ")}.`,
    addingCustom: ["Dodajam orodja iz kataloga", "Berem orodja, ki jih ni na seznamu", "Ocenjujem vsak vnos"],
    customFailed: "Orodja s seznama so dodana. Orodij, ki jih ni na seznamu, nismo mogli dodati. Poskusite znova ali jih dodajte z opisom.",
  },

  chat: {
    title: "Pogovor",
    inputLabel: "Vaš odgovor",
    inputPh: "Napišite odgovor ...",
    send: "Pošlji",
    stop: "Ustavi",
    thinking: "Razmišljam",
    /** the first of the wrap-up's next steps */
    recommendedStep: "Priporočen naslednji korak",
    /** under the reply chips: focuses the text field */
    writeOwn: "ali napišite svoj odgovor",
    /** reply chips where several answers can be true */
    multiHint: "Izberite vse, kar velja.",
    sendPicked: (n: number) => (n ? `Pošlji (${n})` : "Pošlji"),
    /** the text field while reply chips are ticked: the typed text is added to them */
    inputPhPicked: "Dodajte pojasnilo ali pošljite ...",
    /** ticked reply chips as one answer: "A", "A in B", "A, B in C"; typed text after them */
    pickedAnswer: (picked: string[], typed = "") => {
      const list = picked.length > 1 ? `${picked.slice(0, -1).join(", ")} in ${picked[picked.length - 1]}` : (picked[0] ?? "");
      const extra = typed.trim();
      if (!extra) return list;
      return list ? `${list}${/[.!?]$/.test(list) ? "" : "."} ${extra}` : extra;
    },
    pickerTitle: "Označite, kar uporabljate",
    pickerSuggestedHint: "Predlog glede na vaše podjetje",
    pickerConfirm: (n: number) => (n === 0 ? "Nič od tega" : `Potrdi izbor (${n})`),
    pickerSearch: (n: number) => `Išči med ${n} ${plural(n, "orodjem", "orodjema", "orodji", "orodji")}`,
    pickerNoMatch: "Takega orodja ni na seznamu.",
    /** selected tools in one group: "1 izbrano", "2 izbrani" ... */
    pickerPicked: (n: number) => `${n} ${plural(n, "izbrano", "izbrani", "izbrana", "izbranih")}`,
    pickerTools: (n: number) => plural(n, "orodje", "orodji", "orodja", "orodij"),
    /** the user's line after the picker: picked entries (with the account) and tools not on the list */
    pickerMessage: (names: string[], custom = "") =>
      [names.length ? `Uporabljamo: ${names.join(", ")}.` : "Nobenega orodja s seznama ne uporabljamo.", custom].filter(Boolean).join(" "),
    introTitle: "Kaj bo sledilo",
    introItems: [
      "AI vodič vas vpraša, katera AI orodja uporabljate, ali imate lastne agente in kje AI uporabljate za odločitve o ljudeh. Vpraša eno stvar naenkrat.",
      "Kliknite predlog ali odgovorite s svojimi besedami. Česar ne veste, preskočite.",
      "Vnosi se sproti dodajajo v register. Vsak dobi barvo semaforja in ostane predlog, dokler ga ne potrdite.",
    ],
    introTime: "Traja okoli 10 minut. Pogovor lahko prekinete in nadaljujete pozneje.",
    registerTitle: "Register v živo",
    registerEmpty: "Ko omenite orodje ali agenta, se tukaj pojavi kartica.",
    registerOpen: "Odpri register",
    tabChat: "Pogovor",
    tabRegister: (n: number) => `Register (${n})`,
    restart: "Začni znova",
    error: "Pogovor se je prekinil. Poskusite poslati znova.",
    you: "Vi",
    guide: "AI vodič",
    newBadge: "Novo",
    updatedBadge: "Posodobljeno",
    confirmGreen: (n: number) => `Potrdi vse zelene (${n})`,
    confirmHint: "AI vodič vnosov ne potrjuje. Zelene potrdite tukaj, rumene in rdeče odprite v registru in jih preverite.",
    confirmHintNoGreen: "AI vodič vnosov ne potrjuje. Predloge odprite v registru, jih preverite in potrdite.",
    tools: {
      added: (n: number, done: boolean) =>
        `${done ? "Dodal" : "Dodajam"} ${n} ${plural(n, "vnos", "vnosa", "vnose", "vnosov")}`,
      updated: (n: number, done: boolean) =>
        `${done ? "Posodobil" : "Posodabljam"} ${n} ${plural(n, "vnos", "vnosa", "vnose", "vnosov")}`,
      updatedOne: (name: string, done: boolean) => `${done ? "Posodobil" : "Posodabljam"}: ${name}`,
      addedNoCount: (done: boolean) => (done ? "Dodal v register" : "Dodajam v register"),
      updatedNoCount: (done: boolean) => (done ? "Popravil vnos" : "Popravljam vnos"),
      profile: (done: boolean) => (done ? "Dopolnil profil podjetja" : "Dopolnjujem profil podjetja"),
      picker: (done: boolean) => (done ? "Pripravil seznam orodij" : "Pripravljam seznam orodij"),
    },
  },

  register: {
    title: "Register AI",
    add: "Dodaj z opisom",
    addMore: "Dodaj še",
    fromCatalog: "Dodaj iz kataloga",
    import: "Uvozi seznam",
    links: "Povezave za zaposlene",
    filterLight: "Barva",
    filterStatus: "Stanje",
    search: "Išči po imenu",
    colLight: "Semafor",
    colName: "Ime",
    colKind: "Vrsta",
    colOwner: "Lastnik",
    colStatus: "Stanje",
    colReview: "Naslednji pregled",
    noOwner: "Ni določen",
    ownerSuggested: "predlog, potrdite",
    noPurpose: "Namen še ni vpisan",
    runsOn: (names: string) => `Teče na: ${names}`,
    agentsOn: (n: number) => `Agenti na tej platformi: ${n}`,
    empty: "Register je še prazen",
    emptyHint: "Najhitreje ga napolnite v pogovoru z AI vodičem. Orodja lahko dodate tudi iz kataloga ali uvozite seznam.",
    emptyCta: "Začni pogovor",
    noMatch: "Noben vnos ne ustreza filtrom.",
    clearFilters: "Počisti filtre",
    count: (n: number, total: number) => (n === total ? `${total} ${plural(total, "vnos", "vnosa", "vnosi", "vnosov")}` : `${n} od ${total}`),
    addTitle: "Dodaj z opisom",
    addSub: "Naštejte orodja ali opišite agenta. AI pripravi predloge vnosov z oceno.",
    addPh: "Npr.: V marketingu uporabljamo Canvo in ChatGPT, v financah pa n8n za obdelavo računov.",
    addSubmit: "Dodaj v register",
    adding: ["Berem opis", "Iščem orodja v katalogu", "Ocenjujem vsak vnos"],
    added: (n: number) => `Dodano: ${n} ${plural(n, "vnos", "vnosa", "vnosi", "vnosov")}`,
    catalogTitle: "Dodaj iz kataloga",
    catalogSub: "Označite orodja, ki jih uporabljate. Vsako dobi začetno oceno, ki jo nato preverite.",
    catalogAdd: (n: number) => (n ? `Dodaj ${n} ${plural(n, "orodje", "orodji", "orodja", "orodij")}` : "Dodaj orodja"),
    catalogInRegister: "Že v registru",
    importTitle: "Uvozi seznam",
    importSub:
      "Naložite izvoz iz skrbniške konzole ali kateri koli seznam. AI prepozna stolpce in pripravi predloge.",
    importFormats: [
      "Microsoft 365 Agent Registry (CSV)",
      "Power Platform inventar (XLSX)",
      "Google Workspace: dostop aplikacij (CSV)",
      "Kateri koli seznam orodij (CSV, XLSX)",
    ],
    importPick: "Izberite datoteko",
    importDrop: "ali jo povlecite sem",
    importing: ["Berem datoteko", "Prepoznavam stolpce", "Povezujem s katalogom", "Ocenjujem vnose"],
    /** usual duration of a large import in seconds (progress estimate) */
    importEta: 45,
    importLeaveOk: "Okno lahko zaprete in delate drugje v aplikaciji. Uvoz teče naprej.",
    importRunning: (file: string) => `Uvažam ${file} ...`,
    importShow: "Pokaži",
    importDone: (n: number) =>
      n ? `Uvoz je končan. V register smo dodali ${n} ${plural(n, "vnos", "vnosa", "vnose", "vnosov")}.` : "Uvoz je končan. V datoteki nismo našli novih vnosov.",
    importFailed: "Uvoz ni uspel. Poskusite znova.",
    importFailedTitle: "Uvoz ni uspel",
    importPartial: (done: number, total: number) =>
      `${plural(done, "Obdelana", "Obdelani", "Obdelane", "Obdelanih")} ${done} od ${total} vrstic.`,
    importPartialWhy: "Datoteka je daljša, kot jo AI prebere naenkrat. Preostale vrstice shranite v novo datoteko in jo uvozite posebej.",
    importDonePartial: (n: number, done: number, total: number) =>
      `Uvoz je končan: ${n} ${plural(n, "nov vnos", "nova vnosa", "novi vnosi", "novih vnosov")}. ${plural(done, "Obdelana", "Obdelani", "Obdelane", "Obdelanih")} ${done} od ${total} vrstic, preostale uvozite posebej.`,
    importBadType: "Podprti so CSV in XLSX.",
    importStalledTitle: "Uvoz traja dlje kot običajno.",
    importStalled:
      "Strežnik ga morda še obdeluje. Preverite stanje ali uvoz prekličite. Vnosi, ki so že dodani, ostanejo v registru.",
    importStalledToast: "Uvoz traja dlje kot običajno. V registru odprite Uvozi seznam in preverite stanje.",
    importCheck: "Preveri stanje",
    importChecking: "Preverjam ...",
    importCancel: "Prekliči uvoz",
    importCancelling: "Preklicujem ...",
    importCheckFailed: "Stanja zdaj ne moremo preveriti. Poskusite čez minuto.",
    importCancelFailed: "Preklic tokrat ni uspel. Poskusite znova.",
    importCancelled: "Uvoz je preklican.",
    importCancelledKept: (n: number) =>
      n
        ? `Pred preklicem smo v register dodali ${n} ${plural(n, "vnos", "vnosa", "vnose", "vnosov")}. Ti ostanejo.`
        : "Pred preklicem nismo dodali nobenega vnosa.",
    importCancelledToast: (n: number) =>
      n ? `Uvoz je preklican. Že dodani vnosi ostanejo v registru (${n}).` : "Uvoz je preklican.",
    importAgain: "Naloži znova",
    linksTitle: "Povezave za zaposlene",
    linksSub: "Pošljite jih po e-pošti ali objavite na intranetu. Odgovori pridejo v register kot predlogi.",
    linkSurvey: "Anketa o rabi AI",
    linkSurveyHint: "3 do 5 minut, za vse zaposlene.",
    linkIntake: "Prijava nove rabe AI",
    linkIntakeHint: "Ko kdo začne uporabljati novo orodje.",
    linkBuilder: "Semafor za graditelje agentov",
    linkBuilderHint: "Pred zagonom vsakega novega agenta.",
    qrTitle: "QR koda za anketo",
    qrHint: "Za oglasno desko ali predstavitev.",
    proposalsTitle: (n: number) =>
      `${n} ${plural(n, "predlog AI čaka", "predloga AI čakata", "predlogi AI čakajo", "predlogov AI čaka")} na potrditev`,
    proposalsHint: "Zelene lahko potrdite takoj. Rumene in rdeče najprej odprite in preverite korake.",
    proposalsHintNoGreen: "Odprite predlog, preverite korake in ga potrdite. Več predlogov lahko označite in potrdite skupaj.",
    showProposals: "Pokaži samo predloge",
    confirmGreen: (n: number) => `Potrdi vse zelene (${n})`,
    confirmSelected: (n: number) => `Potrdi izbrane (${n})`,
    confirming: "Potrjujem ...",
    selected: (n: number) => `${n} ${plural(n, "izbran", "izbrana", "izbrani", "izbranih")}`,
    clearSelection: "Počisti izbor",
    selectAll: "Izberi vse predloge na seznamu",
    selectRow: (name: string) => `Izberi: ${name}`,
    confirmedToast: (n: number) => `Potrjeno: ${n} ${plural(n, "vnos", "vnosa", "vnosi", "vnosov")}`,
    undone: "Potrditev je razveljavljena.",
  },

  checklist: {
    forSystem: "Za ta sistem",
    forSystemEmpty: "Za ta sistem ni posebnih korakov.",
    open: (n: number) => `še ${n} ${plural(n, "korak", "koraka", "koraki", "korakov")}`,
    allDone: "Vse urejeno",
    baseline: "Velja za vsa AI orodja",
    baselineHint: "Usposabljanje uredite enkrat za celo podjetje.",
    recommendedHint: "Ni obvezno, a prihrani težave.",
    recommendedCount: (n: number) => `Priporočeno (${n})`,
    highRiskNow: "Najprej",
    laterTitle: (n: number, date: string) =>
      `Pozneje: ${n} ${plural(n, "korak", "koraka", "koraki", "korakov")} do ${date}`,
    laterTitleNoDate: (n: number) => `Pozneje: ${n} ${plural(n, "korak", "koraka", "koraki", "korakov")} do roka`,
    laterHint: "Teh korakov zdaj še ni treba urediti. Pripravite jih do roka.",
    nowDoneLater: "Vse, kar velja zdaj, je urejeno. Ostanejo koraki do roka.",
    // the yellow headlines of semafor/triage.py _headline, word for word
    headlineYellow: (n: number) =>
      n === 0
        ? "Uporabljate lahko. Preverite še priporočene korake."
        : `Uporabljate lahko, ko uredite ${n} ${plural(n, "korak", "koraka", "korake", "korakov")}.`,
    headlineLowRisk: (n: number, topic: "gdpr" | "mixed" | "practice") =>
      n === 0
        ? "Uporabljate lahko. Preverite še priporočene korake."
        : `Uporabljate lahko. Uredite še ${n} ${plural(n, "korak", "koraka", "korake", "korakov")} za ${
            { gdpr: "varstvo osebnih podatkov (GDPR)", mixed: "varstvo podatkov in dobro prakso", practice: "dobro prakso" }[topic]
          }.`,
  },

  drawer: {
    close: "Zapri podrobnosti",
    confirm: "Potrdi vnos",
    confirmHint: "Po potrditvi vnos ni več predlog. Naslednji pregled bo čez eno leto.",
    confirmedOn: (d: string) => `Potrjeno ${d}`,
    reviewDue: (d: string) => `Naslednji pregled ${d}`,
    retriage: "Ponovno oceni",
    retriaging: "Ocenjujem ...",
    delete: "Odstrani iz registra",
    deleteTitle: "Odstranim vnos iz registra?",
    deleteText: "Izbrišemo ga z oceno, koraki in zgodovino sprememb. Tega ni mogoče razveljaviti.",
    deleteSubmit: "Odstrani vnos",
    deleting: "Odstranjujem ...",
    tabs: { result: "Ocena", card: "Izkaznica", screening: "Vprašanja", history: "Zgodovina" },
    obligations: "Kaj velja",
    actions: "Koraki",
    actionsDone: (d: number, t: number) => `${d} od ${t} urejeno`,
    openQuestions: "Odprta vprašanja",
    openQuestionsHint: "Odgovori lahko spremenijo barvo. Uredite jih v zavihku Vprašanja.",
    noTriage: "Ta vnos še ni ocenjen.",
    noTriageCta: "Oceni zdaj",
    screeningHint: "Spremenite odgovor in semafor se takoj preračuna.",
    screeningDraft: "Odgovore je predlagal AI iz opisa. Preverite jih, ker vplivajo na barvo.",
    noPurpose: "Namen še ni vpisan.",
    noPurposeCta: "Vpišite ga",
    cardDraft: "Izkaznico je pripravil AI. Črtkana polja so predlogi: preverite oddelke, podatke in lastnika, nato vnos potrdite.",
    historyEmpty: "Še ni zgodovine.",
    by: { ai: "AI" } as Record<string, string>,
    due: (d: string) => `do ${d}`,
    source: "Vir",
    lightChanged: (from: string, to: string) => `Nova barva: ${to} (prej ${from.toLowerCase()})`,
  },

  anketa: {
    title: "Anketa za zaposlene",
    sub: "Zaposleni povedo, katera AI orodja uporabljajo. Iz odgovorov dobite predloge za register in ideje, kje bi AI prihranil čas.",
    linkTitle: "Povezava do ankete",
    responses: "odgovorov",
    responsesLabel: (n: number) => plural(n, "odgovor", "odgovora", "odgovori", "odgovorov"),
    analyze: "Analiziraj odgovore",
    reanalyze: "Analiziraj znova",
    analyzing: (n: number) => [
      `Berem ${n} ${plural(n, "odgovor", "odgovora", "odgovore", "odgovorov")}`,
      "Iščem orodja, ki jih še ni v registru",
      "Preverjam, katere podatke vpisujejo",
      "Zbiram ideje zaposlenih",
    ],
    analyzeHint: "Analiza je smiselna od približno 5 odgovorov naprej.",
    analyzeIntro: "AI poišče orodja, ki jih še ni v registru, tvegano rabo podatkov in ideje zaposlenih. Predloge nato preverite sami.",
    analyzeFailed: "Analiza ni uspela",
    analyzeEta: 30,
    analysisReady: "Analiza ankete je pripravljena.",
    noResponsesHint: "Pošljite povezavo ali vabilo zaposlenim. Ko odgovorijo, lahko odgovore analizirate.",
    aiNote: "Vse spodaj je pripravil AI iz odgovorov zaposlenih. Števila omemb so približna, predloge preverite, preden jih dodate.",
    inviteTitle: "Vabilo za zaposlene",
    inviteHint: "AI napiše kratko vabilo z vašo povezavo. Popravite ga in pošljite iz svoje e-pošte ali v Teams.",
    inviteCta: "Pripravi vabilo",
    inviteDrafting: ["Berem profil podjetja", "Pišem vabilo"],
    inviteSubject: "Zadeva",
    inviteBody: "Besedilo",
    inviteCopy: "Kopiraj",
    inviteMail: "Odpri v e-pošti",
    inviteRedo: "Napiši znova",
    inviteCopied: "Zadeva in besedilo sta kopirana.",
    summary: "Povzetek",
    generatedAt: (d: string) => `Analiza z dne ${d}`,
    partial: (done: number, total: number) =>
      `${plural(done, "Analiziran", "Analizirana", "Analizirani", "Analiziranih")} ${done} od ${total} odgovorov.`,
    partialWhy: "Vseh odgovorov je bilo preveč za eno analizo, zato jih je AI prebral le del. Vsi odgovori ostanejo shranjeni.",
    proposals: "Predlogi za register",
    proposalsHint: "Orodja, ki so jih omenili zaposleni, a jih še ni v registru.",
    proposalsEmpty: "Vsi predlogi so obdelani.",
    accept: "Dodaj v register",
    reject: "Zavrni",
    mentions: (n: number) => `${n} ${plural(n, "omemba", "omembi", "omembe", "omemb")}`,
    findings: "Ugotovitve",
    ideas: "Ideje zaposlenih",
    ideasHint: "Kje bi AI zaposlenim prihranil čas.",
    ctaTitle: "Iz idej v delujoče agente",
    ctaText:
      "Agentcija pomaga izbrati ideje z največjim učinkom in jih zgradi kot agente, ki delajo v vaših sistemih. Prvi pogovor je brezplačen.",
    ctaButton: "Rezerviraj 30-minutni klic",
    accepted: "Dodano v register",
    rejected: "Predlog zavrnjen",
  },

  pismenost: {
    title: "AI pismenost",
    sub: "Evidenca usposabljanj je vaš dokaz za čl. 4.",
    emptyTitle: "Načrt še ni pripravljen",
    emptyText: "AI iz registra in pravilnika pripravi načrt usposabljanja po vlogah. Nato ga preglejte in zaposlenim pošljite povezavo do usposabljanja.",
    generateFailed: "Načrta nismo mogli pripraviti",
    generate: "Pripravi načrt",
    regenerate: "Pripravi znova",
    generating: [
      "Pregledujem orodja v registru",
      "Določam vloge in kdo spada kam",
      "Prilagajam module vašim orodjem",
      "Sestavljam kviz",
    ],
    planEta: 70,
    planReady: "Načrt AI pismenosti je pripravljen.",
    matrixTitle: "Vloge in moduli",
    matrixHint: "Pika pomeni, da modul velja za vlogo.",
    role: "Vloga",
    refresh: "Osvežitev",
    modulesTitle: "Moduli",
    min: (n: number) => `${n} min`,
    quizCount: (n: number) => `${n} ${plural(n, "vprašanje", "vprašanji", "vprašanja", "vprašanj")} v kvizu`,
    linkTitle: "Povezava do usposabljanja",
    linkHint: "Zaposleni prek povezave opravijo osnovni modul in potrdijo pravilnik. Rezultat se zapiše v evidenco.",
    evidenceTitle: "Evidenca usposabljanj",
    evidenceEmpty: "Še nihče ni opravil usposabljanja. Pošljite povezavo zaposlenim.",
    exportEvidence: "Prenesi evidenco (.xlsx)",
    colName: "Ime",
    colDept: "Oddelek",
    colModule: "Modul",
    colScore: "Rezultat",
    colPolicy: "Pravilnik",
    colDate: "Datum",
    colMethod: "Način",
    method: { self_reported: "samoprijava" } as Record<string, string>,
    methodDefault: "samoprijava",
    selfNote: "Zapise vpišejo zaposleni sami prek povezave (samoprijava). Imen ne preverjamo.",
    passed: "Opravljeno",
    failed: "Ni opravljeno",
    ack: "Potrjen",
    noAck: "Ni potrjen",
    summary: (passed: number, total: number) => `Opravljeno: ${passed} od ${total} ${plural(total, "osebe", "oseb", "oseb", "oseb")}`,
    generatedAt: (d: string) => `Načrt z dne ${d}`,
  },

  documents: {
    title: "Dokumenti",
    sub: "Dokumenti nastanejo iz vašega registra. Ko ga spremenite, prenesite novo različico.",
    downloadAll: "Prenesi vse (.zip)",
    download: "Prenesi",
    downloading: "Pripravljam ...",
    unavailable: "Še ni na voljo",
    cardsTitle: "Izkaznice agentov",
    cardsEmpty: "Ko v register dodate lastnega agenta, se tu pojavi njegova izkaznica.",
    failed: "Prenos ni uspel.",
    proposalsNote: (n: number) =>
      `V registru ${plural(n, "je", "sta", "so", "je")} ${n} ${plural(n, "nepotrjen predlog", "nepotrjena predloga", "nepotrjeni predlogi", "nepotrjenih predlogov")} AI. Preglejte ${plural(n, "ga", "ju", "jih", "jih")}, preden dokumente pošljete naprej.`,
    proposalsCta: "Preglej predloge",
    /** the two downloads of a document card */
    downloadPdf: "Prenesi PDF",
    downloadSource: (format: string) => (format === "xlsx" ? "Excel" : format === "docx" ? "Word" : `.${format}`),
    sourceHint: "Za lastne popravke",
    statusMissing: (n: number) => `${plural(n, "Manjka", "Manjkata", "Manjkajo", "Manjka")} še ${n} ${plural(n, "podatek", "podatka", "podatki", "podatkov")}`,
    statusReady: "Pripravljeno",
    statusDraft: "Osnutek",
    statusAdopted: "Sprejet",

    details: {
      title: "Podatki za dokumente",
      sub: "Ti podatki se vpišejo v pravilnik in obvestila. AI jih poišče, vi jih preverite in shranite.",
      done: "Vsi podatki so vpisani. Pravilnik in obvestila sta pripravljena.",
      left: (n: number) => `${plural(n, "Manjka", "Manjkata", "Manjkajo", "Manjka")} še ${n} ${plural(n, "podatek", "podatka", "podatki", "podatkov")}.`,
      suggest: "Predizpolni z AI",
      reading: "Berem vašo spletno stran ...",
      readingSteps: ["Odpiram vašo spletno stran", "Iščem politiko zasebnosti in kontakt", "Iščem zastopnika podjetja"],
      readFailed: "Spletne strani nismo mogli prebrati. Podatke vpišite sami.",
      /** what the website reader filled in: "S spletne strani: politika zasebnosti, kontaktni e-naslov." */
      foundFrom: (items: string[]) => `S spletne strani: ${items.join(", ")}.`,
      foundNone: "Na spletni strani teh podatkov nismo našli. Vpišite jih sami.",
      foundLabels: {
        privacy_url: "politika zasebnosti",
        ai_statement_url: "izjava o rabi AI",
        contact_email: "kontaktni e-naslov",
        signatory_name: "podpisnik",
      } as Record<string, string>,
      missingTag: "Manjka",
      optionalTag: "Ni obvezno",
      /** which register entries need the value */
      neededFor: (names: string[]) => `Potrebno zaradi: ${names.join(", ")}.`,
      signatory: ["Podpisnik", "Običajno direktor ali direktorica. Podpiše pravilnik in obvestila."] as [string, string],
      signatoryNamePh: "Ime in priimek",
      signatoryTitlePh: "Funkcija",
      days: "dni",
      noDomain: "V profilu vpišite spletno stran podjetja, da AI poišče še povezavo do politike zasebnosti in zastopnika.",
      save: "Shrani podatke",
      saved: "Podatki so shranjeni.",
      suggestedNote: "Predloge AI preverite in shranite.",
      groupPolicy: "Pravilnik",
      groupNotices: "Obvestila",
      fields: {
        policy_effective_date: ["Datum začetka veljavnosti", "Predlagamo prvi dan meseca, ki je vsaj 15 dni naprej. Tako je čas tudi za mnenje sindikata."],
        signatory_name: ["Kdo podpiše", "Ime in priimek, običajno direktor ali direktorica."],
        signatory_title: ["Funkcija", ""],
        contact_email: ["Kontaktni e-naslov", "Za vprašanja strank in zaposlenih o rabi AI."],
        privacy_url: ["Povezava do politike zasebnosti", ""],
        ai_statement_url: ["Povezava do izjave o rabi AI", "Stran, kjer objavite izjavo iz dokumenta Besedila obvestil, na primer podjetje.si/ai."],
        assistant_name: ["Ime AI asistenta", "Ime, s katerim se AI predstavi strankam, na primer Nina. Brez imena obvestilo reče »virtualni asistent«."],
        publications_editor: ["Kdo odgovarja za objave", "Oseba ali uredništvo, ki besedila AI pregleda pred objavo."],
        worker_reps: ["Predstavniki delavcev", "Komu pošljete obvestilo, preden AI uvedete pri delu z zaposlenimi."],
        reps_days: ["Rok za pripombe", "Koliko dni imajo predstavniki delavcev za pripombe."],
      } as Record<string, [string, string]>,
      placeholders: {
        signatory_title: "direktorica",
        contact_email: "info@podjetje.si",
        privacy_url: "https://podjetje.si/zasebnost",
        ai_statement_url: "https://podjetje.si/ai",
        worker_reps: "Svet delavcev podjetja ...",
      } as Record<string, string>,
      other: "Drugo, kar dokumenti potrebujejo",
      aiOwner: "Vpišite skrbnika AI v profilu.",
      aiOwnerCta: "Odpri profil",
      training: "Pripravite načrt pismenosti. Povezava na usposabljanje se nato vpiše sama.",
      trainingCta: "Odpri pismenost",
      register: "Obvestila potrebujejo še nekaj podatkov iz registra:",
      registerField: (fields: string[]) => `manjka ${fields.join(", ")}`,
    },

    policy: {
      title: "Sprejem pravilnika",
      sub: "Pravilnik je splošni akt podjetja. Sprejme ga uprava, če imate sindikat, pa pred tem dobi njegovo mnenje.",
      steps: ["Osnutek", "Mnenje sindikata", "Sprejem", "Objava"],
      /** the second step when there is no union */
      stepNoUnion: "Brez sindikata",
      draftText: "Preverite osnutek in ga prenesite kot PDF. Na vsaki strani je oznaka »osnutek«.",
      draftCta: "Prenesi osnutek (PDF)",
      unionQuestion: "Ali je v podjetju sindikat?",
      unionYes: "Da, imamo sindikat",
      unionNo: "Ne, nimamo sindikata",
      unionChange: "Spremeni odgovor",
      consultText:
        "Po 10. členu Zakona o delovnih razmerjih predlog pošljete sindikatu v mnenje. Sindikat ima za mnenje 8 dni, vi pa se morate do njegovega mnenja opredeliti.",
      noUnionText:
        "Ker sindikata nimate, mnenja po 10. členu Zakona o delovnih razmerjih ni treba pridobiti, zato lahko uprava pravilnik sprejme takoj. Priporočamo, da osnutek prej pokažete zaposlenim, če imate svet delavcev, pa tudi njemu.",
      sentOn: "Poslano dne",
      consultCta: "Predlog je poslan",
      consultUntil: (date: string) => `Rok za mnenje: ${date}.`,
      adoptText: "Ko uprava pravilnik sprejme, vpišite datum in podpisnika. Dokumenti se nato pripravijo kot končni.",
      adoptedOn: "Sprejeto dne",
      adoptedBy: "Podpisnik (ime in funkcija)",
      adoptCta: "Pravilnik je sprejet",
      needsStart: "Najprej zgoraj vpišite datum začetka veljavnosti.",
      adopted: (date: string, who: string) => `Pravilnik je sprejet ${date}. Podpisnik: ${who}.`,
      publishText:
        "Zaposlenim pošljite obvestilo iz dokumenta Besedila obvestil in povezavo na usposabljanje. Zaposleni pravilnik potrdijo ob usposabljanju, potrditev se zapiše v evidenco. Sprejeti pravilnik mora biti zaposlenim vedno dostopen, na primer na intranetu ali v skupni mapi.",
      finalCta: "Prenesi pravilnik (PDF)",
      reset: "Začni znova",
      resetDone: "Pravilnik je spet osnutek.",
    },

    confirm: {
      hint: "Potrditev v aplikaciji nadomesti podpis na izkaznici. Ko vnos spremenite, jo ponovite.",
      owner: "Lastnik potrdi",
      admin: "Skrbnik AI potrdi",
      done: (role: string, name: string, date: string) => `${role}: ${name}, ${date}`,
      roleOwner: "Lastnik",
      roleAdmin: "Skrbnik AI",
      undo: "Umakni",
      noOwner: "Najprej vpišite lastnika",
    },
  },

  /** Closed pilot (PILOT_MODE=closed): only allowed e-mails and domains can sign in. */
  pilot: {
    badge: "Zaprt pilot",
    landingNote: "AI semafor je trenutno v zaprtem pilotu za izbrana podjetja. Za dostop pišite na",
    loginNote: "Prijavijo se lahko podjetja, ki sodelujejo v pilotu.",
    checkNote: "Za preverbo agenta se najprej prijavite s službeno e-pošto.",
    deniedTitle: "Zaprt pilot",
    access: {
      title: "Dostop do pilota",
      sub: "Kdo se lahko prijavi, dokler je pilot zaprt. Vpišite e-poštni naslov ali domeno podjetja.",
      modeClosed: "Pilot je zaprt. Prijavijo se lahko samo spodnji naslovi in domene.",
      modeOpen: "Pilot je odprt, prijavi se lahko vsak. Seznam začne veljati, ko pilot zaprete (nastavitev PILOT_MODE na Netlifyju).",
      admins: "Skrbniki Agentcije imajo dostop vedno:",
      entryLabel: "E-pošta ali domena",
      entryPh: "ana@podjetje.si ali @podjetje.si",
      noteLabel: "Opomba",
      notePh: "Npr. ime podjetja (neobvezno)",
      add: "Dodaj",
      adding: "Dodajam ...",
      remove: (entry: string) => `Odstrani ${entry}`,
      fromEnv: "stalno",
      fromEnvHint: "Nastavljeno na Netlifyju (PILOT_ALLOWLIST). Spremenite ga tam.",
      fromEnvNote: "Vnosov z oznako \"stalno\" tukaj ne morete odstraniti. Nastavljeni so na Netlifyju (PILOT_ALLOWLIST).",
      domain: "domena",
      email: "e-pošta",
      empty: "Na seznamu še ni nikogar.",
      added: (entry: string) => `Dodano: ${entry}`,
      removed: (entry: string) => `Odstranjeno: ${entry}`,
      invalid: "Vpišite e-poštni naslov (ana@podjetje.si) ali domeno (@podjetje.si).",
      count: (n: number) => `${n} ${plural(n, "vnos", "vnosa", "vnosi", "vnosov")}`,
    },
  },

  admin: {
    title: "Podjetja",
    colActivity: "Zadnja aktivnost",
    users: "Uporabniki",
    empty: "Še ni podjetij.",
    forbidden: "Ta stran je samo za ekipo Agentcije.",
    startOrg: "Dodaj svoje podjetje",
    myOrg: "Moje podjetje",
    total: (n: number) => `${n} ${plural(n, "podjetje", "podjetji", "podjetja", "podjetij")}`,
    sortBy: "Razvrsti po",
    sortActivity: "zadnji aktivnosti",
    sortSize: "velikosti",
    sortIdeas: "idejah",
    responses: (n: number) => `${n} ${plural(n, "odgovor", "odgovora", "odgovori", "odgovorov")}`,
    ideasTitle: "Ideje zaposlenih",
    noIdeas: "Še ni analize ankete",
    entries: (n: number) => `${n} ${plural(n, "vnos", "vnosa", "vnosi", "vnosov")}`,
    emptyRegister: "Register je prazen",
    lightsLabel: (r: number, y: number, g: number, u: number) =>
      `Rdeča ${r}, rumena ${y}, zelena ${g}${u ? `, neocenjeno ${u}` : ""}`,
    activity: (d: string) => `Aktivno ${d}`,
    created: (d: string) => `Ustvarjeno ${d}`,
    more: "Podrobnosti",
    moreIdeas: (n: number) => `in še ${n} ${plural(n, "ideja", "ideji", "ideje", "idej")}`,
  },

  demo: {
    loginDemo: "Prijava v demo podjetje",
    fresh: "Novo podjetje (uvod)",
    anon: "Odjavljen",
    pilotOn: "Zaprt pilot: vklopi",
    pilotOff: "Zaprt pilot: izklopi",
    revoke: "Umakni moj dostop do pilota",
    conflict: "Naslednja sprememba vnosa: konflikt",
    hide: "Skrij",
  },
};

export type Strings = typeof sl;
/** The strings in the company's term ("AI" or "UI"): see termLayer.ts. */
export const t: Strings = withTerm(sl);
